// Trail data of the app — the data part of TrailBivvy/App/AppEnvironment.swift (catalogue, followed trail,
// reloadData, followTrail, stopFollowingTrail, dataset(forTrail:), imports). The live store merges, in this order:
// the world regulatory layer, the followed trail's pack, every offline region dataset and every user import
// (ARCHITECTURE.md §4.5). A generation counter drops results of superseded reloads.

import { computed, signal } from '@preact/signals';
import type { TrailCatalogEntry, TrailDataset, TrailStage } from '../core/models';
import { fetchDataJSON, loadDataset, loadIndex, loadWorldRegulatoryLayer, type DataLoader } from '../data/catalog';
import { decodeTrailDataset, encodeTrailDataset } from '../data/decode';
import { mergeDatasets } from '../data/merge';
import { TrailDataStore } from '../data/store';
import { userImportProvenance } from '../core/domain';
import { importFileObject, makeDataset, type ImportedGeoData } from '../import';
import { kvStore, type KeyValueStore } from '../storage/idb';
import { DemoTrailPolicy, TrailChoicePolicy } from './policies';

export interface DataControllerDeps {
  loader?: DataLoader;
  imports?: KeyValueStore<unknown>;
  /** Datasets of completed offline downloads (src/offline). */
  offlineDatasets?: () => Promise<TrailDataset[]>;
  /** Persisted followed trail (prefs.activeTrailID). */
  storedTrailID: { value: string | undefined };
}

export class DataController {
  readonly catalog = signal<TrailCatalogEntry[]>([]);
  readonly catalogLoaded = signal(false);
  readonly activeTrailID = signal<string | undefined>(undefined);
  readonly dataStore = signal<TrailDataStore>(TrailDataStore.empty());
  readonly dataLoadError = signal<string | undefined>(undefined);
  readonly loadingTrailID = signal<string | undefined>(undefined);
  readonly followError = signal<string | undefined>(undefined);
  readonly imports = signal<TrailDataset[]>([]);
  /** Bumped after each completed reload (screens re-read derived data). */
  readonly generation = signal(0);

  readonly activeTrailEntry = computed(() => this.catalog.value.find((e) => e.id === this.activeTrailID.value));
  /** The followed trail once its data is in the store — never another trail in its place. */
  readonly primaryTrail = computed(() => {
    const id = this.activeTrailID.value;
    return id ? this.dataStore.value.trail(id) : undefined;
  });
  readonly importedTrails = computed(() => this.dataStore.value.trails.filter((t) => t.kind === 'imported'));
  /** No trail, no imported route: the no-trail prompts show instead of any data (iOS #14). */
  readonly followsNoTrail = computed(() => !this.activeTrailID.value && this.imports.value.length === 0);

  private world: TrailDataset | undefined;
  private reloadGeneration = 0;
  private readonly loader: DataLoader;
  private readonly importStore: KeyValueStore<unknown>;

  constructor(private readonly deps: DataControllerDeps) {
    this.loader = deps.loader ?? fetchDataJSON;
    this.importStore = deps.imports ?? kvStore('imports');
  }

  async start(): Promise<void> {
    try {
      const index = await loadIndex(this.loader);
      this.catalog.value = DemoTrailPolicy.listedEntries(index.entries);
    } catch (error) {
      this.dataLoadError.value = String(error instanceof Error ? error.message : error);
    }
    this.catalogLoaded.value = true;
    this.world = await loadWorldRegulatoryLayer(this.loader);
    this.imports.value = await this.loadImports();
    const initial = TrailChoicePolicy.initialTrailID(this.deps.storedTrailID.value, (id) =>
      this.catalog.value.some((e) => e.id === id),
    );
    // A stored trail no longer in the catalogue is dropped, never replaced (iOS #14).
    if (initial !== this.deps.storedTrailID.value) this.deps.storedTrailID.value = initial;
    this.activeTrailID.value = initial;
    await this.reloadData();
  }

  /** Rebuilds the live store. */
  async reloadData(): Promise<void> {
    const generation = ++this.reloadGeneration;
    const entry = this.activeTrailEntry.value;
    let loadError: string | undefined;
    let pack: TrailDataset | undefined;
    if (entry) {
      try {
        pack = await loadDataset(entry, this.loader);
      } catch (error) {
        loadError = String(error instanceof Error ? error.message : error);
      }
    }
    const offline = (await this.deps.offlineDatasets?.().catch(() => [])) ?? [];
    if (generation !== this.reloadGeneration) return;
    let merged: TrailDataset | undefined = this.world;
    for (const dataset of [pack, ...offline.filter((d) => !d.metadata.isSynthetic), ...this.imports.value]) {
      if (!dataset) continue;
      merged = merged ? mergeDatasets(merged, dataset) : dataset;
    }
    this.dataStore.value = merged ? new TrailDataStore(merged) : TrailDataStore.empty();
    this.dataLoadError.value = loadError;
    this.generation.value += 1;
  }

  /** Full dataset of any catalogue trail, for browsing without touching the live store. */
  datasetForTrail(entry: TrailCatalogEntry): Promise<TrailDataset | undefined> {
    return loadDataset(entry, this.loader);
  }

  /** Follows another trail; restores the previous one if the pack cannot be loaded. */
  async followTrail(id: string): Promise<boolean> {
    const previous = this.activeTrailID.value;
    this.followError.value = undefined;
    this.loadingTrailID.value = id;
    this.activeTrailID.value = id;
    this.deps.storedTrailID.value = id;
    await this.reloadData();
    this.loadingTrailID.value = undefined;
    if (this.dataLoadError.value && !this.dataStore.value.trail(id)) {
      this.followError.value = this.dataLoadError.value;
      this.activeTrailID.value = previous;
      this.deps.storedTrailID.value = previous;
      await this.reloadData();
      return false;
    }
    return true;
  }

  async stopFollowingTrail(): Promise<void> {
    this.activeTrailID.value = undefined;
    this.deps.storedTrailID.value = undefined;
    await this.reloadData();
  }

  /** Stages of the followed trail (or of the only imported route when no trail is followed). */
  primaryStages(): TrailStage[] {
    const store = this.dataStore.value;
    const id = this.activeTrailID.value;
    if (id) return store.stagesForTrail(id);
    const imported = this.importedTrails.value[0];
    return imported ? store.stagesForTrail(imported.id) : [];
  }

  /** Keeps an imported dataset on the device and merges it into the live store. */
  async addImport(dataset: TrailDataset): Promise<void> {
    await this.importStore.set(dataset.metadata.id, encodeTrailDataset(dataset));
    this.imports.value = [...this.imports.value.filter((d) => d.metadata.id !== dataset.metadata.id), dataset];
    await this.reloadData();
  }

  /** GPX / GeoJSON / KML chosen by the hiker (AppEnvironment.importFile): imported, kept on the device, merged. */
  async importFile(file: File, now: number): Promise<ImportedGeoData> {
    const imported = await importFileObject(file, now);
    const dataset = makeDataset(imported, {
      id: `import:${crypto.randomUUID()}`,
      name: file.name,
      version: '1',
      generatedAt: now,
      isSynthetic: false,
      provenance: [userImportProvenance(file.name, now)],
    });
    await this.addImport(dataset);
    return imported;
  }

  async removeImport(id: string): Promise<void> {
    await this.importStore.delete(id);
    this.imports.value = this.imports.value.filter((d) => d.metadata.id !== id);
    await this.reloadData();
  }

  private async loadImports(): Promise<TrailDataset[]> {
    const out: TrailDataset[] = [];
    try {
      for (const json of await this.importStore.values()) {
        try {
          out.push(decodeTrailDataset(json));
        } catch {
          // an unreadable import is skipped, not fatal
        }
      }
    } catch {
      // storage unavailable
    }
    return out;
  }
}
